import React from 'react';
import { Shield, Mail, Lock, FileText, Server, AlertCircle } from 'lucide-react';

/**
 * Iter 82 — Internal Privacy Policy for Ellora EPC ERP.
 *
 * This page exists primarily to satisfy Google Play Console's mandatory
 * "Privacy Policy URL" requirement when publishing the Ellora EPC ERP
 * Android app. The ERP is an INTERNAL-ONLY application — not distributed
 * publicly — and processes only Ellora EPC's own operational data.
 *
 * Available unauthenticated at: /privacy and /privacy-policy
 */
export default function PrivacyPolicy() {
  const lastUpdated = '19 May 2026';
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#3b4872] flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.25em] text-[#3b4872]">Ellora EPC Pvt Ltd</div>
              <h1 className="font-display font-bold text-base text-slate-900">Privacy Policy</h1>
            </div>
          </div>
          <a href="/app" className="text-xs text-[#3b4872] font-display font-semibold hover:underline">Back to App →</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 prose-styled" data-testid="privacy-policy-page">
        <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 mb-8 rounded-sm flex gap-3">
          <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Internal Use Only.</strong> This application is operated by Ellora EPC Pvt Ltd for the exclusive use of its own employees, contractors, and authorised vendors. It is not offered to the general public and is not available on consumer app stores under a public listing.
          </div>
        </div>

        <p className="text-xs text-slate-500 mb-6">Last updated: {lastUpdated}</p>

        <Section icon={FileText} title="1. Scope">
          <p>This Privacy Policy explains how the Ellora EPC ERP application (the “App”) — provided to authorised personnel of Ellora EPC Pvt Ltd (“Ellora EPC”, “we”, “us”) — collects, uses, and protects information when employees, contractors, vendors, or other authorised users (each a “User”) access the App on web or Android devices.</p>
          <p>By signing in to the App, the User acknowledges and accepts this Policy. Access is granted solely under the User’s employment, contractor, or vendor relationship with Ellora EPC, and may be revoked at any time.</p>
        </Section>

        <Section icon={Server} title="2. Information We Collect">
          <p>To operate the ERP, the App collects only the data needed to deliver business workflows:</p>
          <ul>
            <li><strong>Account & profile data</strong> — name, work email, phone, employee ID, department, designation, reporting manager, RBAC role.</li>
            <li><strong>Operational data</strong> — projects, BOQ, WBS, daily progress reports, site visit reports, procurement records, RA bills, attendance, HSE incidents, tasks, dependencies, and approvals you create or interact with.</li>
            <li><strong>Device & location data</strong> — GPS coordinates and timestamp when you punch in/out or capture a site visit; device model, OS version, and IP address for security auditing.</li>
            <li><strong>Camera & media</strong> — photos / videos you explicitly capture for DPR, SVR, HSE incidents, or face-verified attendance. Face descriptors used for verification are stored as encoded vectors, not raw images.</li>
            <li><strong>Files you upload</strong> — drawings, documents, invoices, and other attachments stored in our project storage.</li>
            <li><strong>Authentication tokens</strong> — JWT session tokens kept securely on your device for the duration of your active session; logging out invalidates the token server-side.</li>
            <li><strong>Diagnostic & audit logs</strong> — anonymised request logs (endpoint, status, latency) for security monitoring; full audit trails of who created, modified, or deleted records.</li>
          </ul>
          <p>The App <strong>does not</strong> collect contacts, SMS, call logs, browsing history outside the App, advertising identifiers, or biometric data from your device’s hardware (face/fingerprint sensors stay on-device under Android’s BiometricPrompt API).</p>
        </Section>

        <Section icon={Shield} title="3. How We Use the Data">
          <p>All data is used strictly for legitimate business operations:</p>
          <ul>
            <li>Running ERP workflows: project execution, procurement, billing, HR, attendance, quality, HSE.</li>
            <li>Calculating cost performance, EVM metrics, and management reports.</li>
            <li>Enforcing access control, audit trails, and statutory compliance.</li>
            <li>Sending in-app notifications, optional email digests, and approval prompts.</li>
            <li>Investigating security incidents and preventing misuse.</li>
          </ul>
          <p>We do <strong>not</strong> sell, rent, monetise, or share your data with advertisers or unaffiliated third parties.</p>
        </Section>

        <Section icon={Lock} title="4. Data Storage & Security">
          <ul>
            <li>Data is stored on infrastructure managed by Ellora EPC and its hosting providers, with encryption in transit (HTTPS / TLS) and at rest.</li>
            <li>Passwords are stored as bcrypt hashes, never in plain text.</li>
            <li>Session tokens are JWT-based with a server-side denylist on logout (active-session control).</li>
            <li>RBAC + department-scoped access ensures users only see data relevant to their role.</li>
            <li>Security findings from periodic audits are tracked and remediated.</li>
          </ul>
          <p>Despite our safeguards, no system is 100% secure. Users should report any suspected breach immediately to the IT team.</p>
        </Section>

        <Section icon={FileText} title="5. Data Retention">
          <p>Operational records are retained for the duration of the User’s engagement with Ellora EPC and for periods mandated by Indian statutory, contractual, and audit requirements (e.g. Companies Act, Income Tax Act, GST, EPF, labour laws). Project and financial records are typically retained for at least 8 years post-completion.</p>
          <p>Upon termination of engagement, a User’s account is deactivated; personal data may be anonymised or deleted upon written request to <strong>hr@elloraepc.com</strong>, subject to overriding legal-retention obligations.</p>
        </Section>

        <Section icon={Shield} title="6. Permissions Requested by the Android App">
          <p>The App requests Android permissions only when necessary, and only when the User initiates the corresponding action:</p>
          <ul>
            <li><strong>Camera</strong> — to capture DPR/SVR/HSE photos and for face-verified attendance.</li>
            <li><strong>Location (fine)</strong> — to geo-stamp punch-in/out and site visit reports.</li>
            <li><strong>Storage / Photos</strong> — to upload attachments you choose.</li>
            <li><strong>Internet & Network state</strong> — to reach the ERP backend.</li>
            <li><strong>Notifications (Android 13+)</strong> — for in-app pings on tasks, approvals, and alerts.</li>
          </ul>
          <p>All permissions can be revoked from Android Settings → Apps → Ellora EPC ERP → Permissions at any time.</p>
        </Section>

        <Section icon={FileText} title="7. Children's Privacy">
          <p>The App is intended for adult employees and authorised personnel only. It is not directed to children under 18. We do not knowingly collect personal data from minors.</p>
        </Section>

        <Section icon={FileText} title="8. Changes to This Policy">
          <p>We may update this Policy from time to time. Material changes will be notified inside the App and via email to active Users. Continued use after the effective date constitutes acceptance.</p>
        </Section>

        <Section icon={Mail} title="9. Contact Us">
          <p>For any questions, data-access or deletion requests, or grievances regarding privacy, please contact:</p>
          <div className="bg-white border border-slate-200 rounded-sm p-4 my-3 text-sm">
            <div className="font-display font-bold text-slate-900">Ellora EPC Pvt Ltd</div>
            <div className="text-slate-600 mt-1">Grievance Officer · Data Privacy</div>
            <div className="text-slate-600 mt-2">Email: <a className="text-[#3b4872] font-semibold" href="mailto:privacy@elloraepc.com">privacy@elloraepc.com</a></div>
            <div className="text-slate-600">Website: <a className="text-[#3b4872] font-semibold" href="https://elloraepc.in" target="_blank" rel="noreferrer">https://elloraepc.in</a></div>
          </div>
        </Section>

        <footer className="border-t border-slate-200 mt-10 pt-6 pb-12 text-xs text-slate-500">
          © {new Date().getFullYear()} Ellora EPC Pvt Ltd. All rights reserved. This Policy is governed by the laws of India and any disputes shall be subject to the exclusive jurisdiction of courts in Pune, Maharashtra.
        </footer>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-8">
      <h2 className="flex items-center gap-2 font-display font-bold text-base text-slate-900 mb-3 mt-6">
        <Icon size={15} className="text-[#3b4872]" />{title}
      </h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
        {children}
      </div>
    </section>
  );
}
